import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { BarChart3, Search } from "lucide-react";
import { ChatDock } from "@/components/ChatDock";
import { MobileTopBar } from "@/components/MobileTopBar";
import ChartCarousel from "@/components/ChartCarousel";
import {
  fetchMonthlyUtilisation,
  fetchAvgCor,
  fetchNonChargeableGbp,
  fetchUtilisationByLocation,
  fetchNonChargeableHoursByActivity,
  fetchNonChargeableGbpByService,
  fetchUtilisationByGrade,
  fetchBurnoutRisk,
  fetchFilterOptions,
  fetchDateRange,
  getDefaultDateRange,
} from "@/analytics/api";
import type { UtilisationFilters, FilterOptions } from "@/analytics/api";
import { getVisibleCharts } from "@/analytics/chart.config";
import { contextFromDatasets } from "@/analytics/charts/chart.context";
import type { ChartContextSource, ChartContextUnit } from "@/analytics/charts/chart.context";
import { EMPLOYEE_GRADE_ORDER } from "@/analytics/chart.utilities";
import UtilisationFilterBar from "@/components/UtilisationFilterBar";
import { NoAccess } from "@/components/NoAccess";
import { isForbiddenError } from "@/services/apiClient";
import UtilisationSkeleton from "@/components/UtilisationSkeleton";
import { makeFilterCache } from "@/lib/filterPersistence";
import { useChatVisibility } from "@/hooks/useChatVisibility";

const filterCache = makeFilterCache<UtilisationFilters>("prism:utilisation:filters");

const metricTitles: Record<string, string> = {
  "utilisation-rate": "Utilisation",
  "recovery-rate": "Recovery Rate",
  "wip-aged": "WIP (Aged)",
  "debt-aged": "Debtors (Aged)",
};

// Chart keys grouped by their data source
const ALL_CHART_KEYS = [
  "monthlyUtilisationCombo",
  "averageCor",
  "nonChargeableHoursByActivity",
  "utilisationByLocation",
  "nonChargeableHoursByActivityBreakdown",
  "nonChargeableGbpByService",
  "utilisationByGrade",
  "burnoutRisk",
];
const UTILISATION_FILTERS_STORAGE_KEY = "prism:utilisation:filters";
const EMPTY_UTILISATION_FILTERS: UtilisationFilters = { legacy_location: [], employees: [], emp_categories: [], service_lines: [], date_from: null, date_to: null };

function sanitiseUtilPct(value: number | null | undefined, monthLabel: string): number | null {
  if (value === null || value === undefined) return null;
  if (value < 0 || value > 150) {
    console.warn(`Outlier utilisation_pct for ${monthLabel}: ${value} — omitting data point`);
    return null;
  }
  return value;
}

// Only these grades are shown, in EMPLOYEE_GRADE_ORDER's seniority order —
// drops anything the backend returns outside that set (e.g. the NA sentinel).
function buildUtilByGradeDataset(payload: {
  data: { grade: string; util_pct: number | null; forecast_util_pct?: number | null }[];
  average_util_pct: number | null;
  forecast_average_util_pct?: number | null;
}) {
  const byGrade = new Map(payload.data.map(d => [d.grade, d]));
  const orderedGrades = EMPLOYEE_GRADE_ORDER.filter(grade => byGrade.has(grade));
  const values = orderedGrades
    .map(grade => byGrade.get(grade)?.util_pct)
    .filter((v): v is number => v !== null && v !== undefined);
  const averageUtilPct = values.length
    ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100
    : null;
  const forecastValues = orderedGrades
    .map(grade => byGrade.get(grade)?.forecast_util_pct)
    .filter((v): v is number => v !== null && v !== undefined);
  const forecastAverageUtilPct = forecastValues.length
    ? Math.round((forecastValues.reduce((a, b) => a + b, 0) / forecastValues.length) * 100) / 100
    : null;
  return {
    labels: orderedGrades,
    datasets: [
      { label: "Utilisation %", data: orderedGrades.map(grade => byGrade.get(grade)?.util_pct ?? null) },
      { label: "Forecast Utilisation %", data: orderedGrades.map(grade => byGrade.get(grade)?.forecast_util_pct ?? null) },
    ],
    averageUtilPct,
    forecastAverageUtilPct,
  };
}

// Wide monthly rows ({ month_label, [seriesName]: value }) -> one line per
// series. Shared by the non-chargeable-hours-by-activity and
// non-chargeable-£-by-service-line charts, which are shaped identically.
function buildMonthlySeriesDataset(payload: { data: Record<string, unknown>[]; series: string[] }) {
  return {
    labels: payload.data.map(d => d.month_label as string),
    datasets: payload.series.map(seriesName => ({
      label: seriesName,
      data: payload.data.map(d => (d[seriesName] as number | null) ?? null),
    })),
  };
}

// Units per chart, so the model can tell pounds from hours from percentages.
// Note nonChargeableHoursByActivity is monthly GBP despite its name.
const UTILISATION_CHART_UNITS: Record<string, ChartContextUnit | undefined> = {
  averageCor: "gbp",
  utilisationByLocation: "percent",
  nonChargeableHoursByActivity: "gbp",
  nonChargeableHoursByActivityBreakdown: "hours",
  nonChargeableGbpByService: "gbp",
  utilisationByGrade: "percent",
  burnoutRisk: "percent",
};

const MetricDetail = () => {
  const { metricSlug } = useParams();
  const metricTitle = metricTitles[metricSlug || ""] || "Metric Details";

  const [isAssistantOpen, setIsAssistantOpen] = useChatVisibility();
  const [chartData, setChartData] = useState<any>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [noAccess, setNoAccess] = useState(false);
  const [filtersReady, setFiltersReady] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());
  const [chartErrors, setChartErrors] = useState<Record<string, boolean>>({});
  const [userRole, setUserRole] = useState<string>("");
  const [filters, setFilters] = useState<UtilisationFilters>(() => filterCache.read() ?? EMPTY_UTILISATION_FILTERS);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    legacy_location: [],
    employees: [],
    emp_categories: [],
    service_lines: [],
    min_month: null,
    max_month: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const optionsAbortControllerRef = useRef<AbortController | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [validatedFilterKey, setValidatedFilterKey] = useState<string | null>(null);
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setUserRole(user.role);
    }
  }, []);

  useEffect(() => { filterCache.write(filters); }, [filters]);

  useEffect(() => {
    if (metricSlug !== "utilisation-rate") return;
    Promise.all([fetchFilterOptions(EMPTY_UTILISATION_FILTERS), fetchDateRange()])
      .then(([options, dateRange]) => {
        const { min_month, max_month } = dateRange;
        const { date_from, date_to } = getDefaultDateRange(min_month, max_month);
        setFilterOptions({ ...options, min_month, max_month });
        setFilters(prev => {
          if (!prev.date_from && !prev.date_to) return { ...prev, date_from, date_to };
          return prev.date_to === date_to ? prev : { ...prev, date_to };
        });
        setFiltersReady(true);
      })
      .catch(err => {
        console.error("Error loading filter options:", err);
        // Still mark ready so charts load without a date filter
        setFiltersReady(true);
      });
  }, [metricSlug]);

  useEffect(() => {
    if (metricSlug !== "utilisation-rate" || !filtersReady) return;
    const defaults = getDefaultDateRange(filterOptions.min_month, filterOptions.max_month);
    const isDefault = !filters.legacy_location.length && !filters.employees.length && !filters.emp_categories.length && !filters.service_lines.length && filters.date_from === defaults.date_from && filters.date_to === defaults.date_to;
    try { isDefault ? localStorage.removeItem(UTILISATION_FILTERS_STORAGE_KEY) : localStorage.setItem(UTILISATION_FILTERS_STORAGE_KEY, JSON.stringify(filters)); } catch { /* no-op */ }
    optionsAbortControllerRef.current?.abort();
    const controller = new AbortController();
    optionsAbortControllerRef.current = controller;
    const requestKey = JSON.stringify(filters);
    setOptionsLoading(true);
    setValidatedFilterKey(null);
    const timeout = window.setTimeout(() => {
      fetchFilterOptions(filters, controller.signal)
        .then(options => { if (!controller.signal.aborted) { setFilterOptions(prev => ({ ...options, min_month: prev.min_month, max_month: prev.max_month })); setValidatedFilterKey(requestKey); } })
        .catch(err => { if (!controller.signal.aborted && err?.name !== "AbortError") { console.error("Error refreshing utilisation filter options:", err); setValidatedFilterKey(requestKey); } })
        .finally(() => { if (!controller.signal.aborted) setOptionsLoading(false); });
    }, 250);
    return () => { window.clearTimeout(timeout); controller.abort(); };
  }, [metricSlug, filtersReady, filters]);

  useEffect(() => {
    if (metricSlug !== "utilisation-rate" || !filtersReady || validatedFilterKey !== JSON.stringify(filters)) return;
    loadUtilisationData(filters);
  }, [metricSlug, filtersReady, filters, validatedFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUtilisationData = (activeFilters: UtilisationFilters) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;
    setNoAccess(false);

    const isFirst = !hasLoadedOnce.current;

    if (isFirst) {
      setInitialLoading(true);
    } else {
      setLoadingKeys(new Set(ALL_CHART_KEYS));
      setChartErrors({});
    }

    const buildNcDataset = (data: any[]) => ({
      labels: data.map((d: any) => d.monthLabel),
      datasets: [{ label: "Non-Chargeable £", data: data.map((d: any) => d.non_chargeable_gbp) }],
    });

    if (isFirst) {
      // Wait for all sources then set state once — keeps first-load atomic
      Promise.all([
        fetchMonthlyUtilisation(activeFilters, signal),
        fetchAvgCor(activeFilters, signal),
        fetchNonChargeableGbp(activeFilters, signal),
        fetchUtilisationByLocation(activeFilters, signal),
        fetchNonChargeableHoursByActivity(activeFilters, signal),
        fetchNonChargeableGbpByService(activeFilters, signal),
        fetchUtilisationByGrade(activeFilters, signal),
        fetchBurnoutRisk(activeFilters, signal),
      ])
        .then(([monthly, avgCor, nc, utilByLocation, activityHours, gbpByService, utilByGrade, burnoutRisk]) => {
          if (signal.aborted) return;
          setChartData({
            monthlyUtilisationCombo: {
              labels: monthly.data.map((d: any) => d.monthLabel),
              chargeableHours: monthly.data.map((d: any) => d.chargeable_hours),
              non_chargeableHours: [],
              utilisationPct: monthly.data.map((d: any) => sanitiseUtilPct(d.util_pct, d.monthLabel)),
              plannedChargeableHours: monthly.data.map((d: any) => d.planned_chargeable_hours ?? 0),
              forecastUtilisationPct: monthly.data.map((d: any) => d.forecast_util_pct ?? 0),
            },
            averageCor: {
              labels: avgCor.data.map((d: any) => d.monthLabel),
              datasets: [{ label: "Average COR", data: avgCor.data.map((d: any) => d.avg_cor) }],
            },
            nonChargeableHoursByActivity: buildNcDataset(nc.data),
            utilisationByLocation: buildMonthlySeriesDataset(utilByLocation),
            nonChargeableHoursByActivityBreakdown: buildMonthlySeriesDataset(activityHours),
            nonChargeableGbpByService: buildMonthlySeriesDataset(gbpByService),
            utilisationByGrade: buildUtilByGradeDataset(utilByGrade),
            burnoutRisk: {
              labels: burnoutRisk.data.map((d: any) => d.monthLabel),
              datasets: [{ label: "At-Risk %", data: burnoutRisk.data.map((d: any) => d.at_risk_pct) }],
            },
          });
          hasLoadedOnce.current = true;
        })
        .catch(err => {
          if (signal.aborted || err?.name === "AbortError") return;
          if (isForbiddenError(err)) setNoAccess(true);
          else console.error("Error loading utilisation data:", err);
        })
        .finally(() => {
          if (!signal.aborted) setInitialLoading(false);
        });
      return;
    }

    // ── Non-destructive updates: each data source updates independently ──

    // Monthly combo chart
    fetchMonthlyUtilisation(activeFilters, signal)
      .then(monthly => {
        if (signal.aborted) return;
        setChartData((prev: any) => ({
          ...prev,
          monthlyUtilisationCombo: {
            labels: monthly.data.map((d: any) => d.monthLabel),
            chargeableHours: monthly.data.map((d: any) => d.chargeable_hours),
            non_chargeableHours: [],
            utilisationPct: monthly.data.map((d: any) => sanitiseUtilPct(d.util_pct, d.monthLabel)),
            plannedChargeableHours: monthly.data.map((d: any) => d.planned_chargeable_hours ?? 0),
            forecastUtilisationPct: monthly.data.map((d: any) => d.forecast_util_pct ?? 0),
          },
        }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("monthlyUtilisationCombo"); return n; });
      })
      .catch(err => {
        if (signal.aborted || err?.name === "AbortError") return;
        if (isForbiddenError(err)) { setNoAccess(true); return; }
        setChartErrors(prev => ({ ...prev, monthlyUtilisationCombo: true }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("monthlyUtilisationCombo"); return n; });
      });

    // Avg COR chart
    fetchAvgCor(activeFilters, signal)
      .then(avgCor => {
        if (signal.aborted) return;
        setChartData((prev: any) => ({
          ...prev,
          averageCor: {
            labels: avgCor.data.map((d: any) => d.monthLabel),
            datasets: [{ label: "Average COR", data: avgCor.data.map((d: any) => d.avg_cor) }],
          },
        }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("averageCor"); return n; });
      })
      .catch(err => {
        if (signal.aborted || err?.name === "AbortError") return;
        if (isForbiddenError(err)) { setNoAccess(true); return; }
        setChartErrors(prev => ({ ...prev, averageCor: true }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("averageCor"); return n; });
      });

    // Non-chargeable monthly chart
    fetchNonChargeableGbp(activeFilters, signal)
      .then(nc => {
        if (signal.aborted) return;
        setChartData((prev: any) => ({
          ...prev,
          nonChargeableHoursByActivity: buildNcDataset(nc.data),
        }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("nonChargeableHoursByActivity"); return n; });
      })
      .catch(err => {
        if (signal.aborted || err?.name === "AbortError") return;
        if (isForbiddenError(err)) { setNoAccess(true); return; }
        setChartErrors(prev => ({ ...prev, nonChargeableHoursByActivity: true }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("nonChargeableHoursByActivity"); return n; });
      });

    // Utilisation % by location
    fetchUtilisationByLocation(activeFilters, signal)
      .then(utilByLocation => {
        if (signal.aborted) return;
        setChartData((prev: any) => ({
          ...prev,
          utilisationByLocation: buildMonthlySeriesDataset(utilByLocation),
        }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("utilisationByLocation"); return n; });
      })
      .catch(err => {
        if (signal.aborted || err?.name === "AbortError") return;
        if (isForbiddenError(err)) { setNoAccess(true); return; }
        setChartErrors(prev => ({ ...prev, utilisationByLocation: true }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("utilisationByLocation"); return n; });
      });

    // Non-chargeable hours by activity
    fetchNonChargeableHoursByActivity(activeFilters, signal)
      .then(activityHours => {
        if (signal.aborted) return;
        setChartData((prev: any) => ({
          ...prev,
          nonChargeableHoursByActivityBreakdown: buildMonthlySeriesDataset(activityHours),
        }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("nonChargeableHoursByActivityBreakdown"); return n; });
      })
      .catch(err => {
        if (signal.aborted || err?.name === "AbortError") return;
        if (isForbiddenError(err)) { setNoAccess(true); return; }
        setChartErrors(prev => ({ ...prev, nonChargeableHoursByActivityBreakdown: true }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("nonChargeableHoursByActivityBreakdown"); return n; });
      });

    // Non-chargeable GBP by service line
    fetchNonChargeableGbpByService(activeFilters, signal)
      .then(gbpByService => {
        if (signal.aborted) return;
        setChartData((prev: any) => ({
          ...prev,
          nonChargeableGbpByService: buildMonthlySeriesDataset(gbpByService),
        }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("nonChargeableGbpByService"); return n; });
      })
      .catch(err => {
        if (signal.aborted || err?.name === "AbortError") return;
        if (isForbiddenError(err)) { setNoAccess(true); return; }
        setChartErrors(prev => ({ ...prev, nonChargeableGbpByService: true }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("nonChargeableGbpByService"); return n; });
      });

    // Utilisation % by grade
    fetchUtilisationByGrade(activeFilters, signal)
      .then(utilByGrade => {
        if (signal.aborted) return;
        setChartData((prev: any) => ({
          ...prev,
          utilisationByGrade: buildUtilByGradeDataset(utilByGrade),
        }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("utilisationByGrade"); return n; });
      })
      .catch(err => {
        if (signal.aborted || err?.name === "AbortError") return;
        if (isForbiddenError(err)) { setNoAccess(true); return; }
        setChartErrors(prev => ({ ...prev, utilisationByGrade: true }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("utilisationByGrade"); return n; });
      });

    // Burnout risk %
    fetchBurnoutRisk(activeFilters, signal)
      .then(burnoutRisk => {
        if (signal.aborted) return;
        setChartData((prev: any) => ({
          ...prev,
          burnoutRisk: {
            labels: burnoutRisk.data.map((d: any) => d.monthLabel),
            datasets: [{ label: "At-Risk %", data: burnoutRisk.data.map((d: any) => d.at_risk_pct) }],
          },
        }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("burnoutRisk"); return n; });
      })
      .catch(err => {
        if (signal.aborted || err?.name === "AbortError") return;
        if (isForbiddenError(err)) { setNoAccess(true); return; }
        setChartErrors(prev => ({ ...prev, burnoutRisk: true }));
        setLoadingKeys(prev => { const n = new Set(prev); n.delete("burnoutRisk"); return n; });
      });
  };

  // Per-chart retry — re-fetches only the data source for the errored chart
  const retryChart = (dataKey: string) => {
    setChartErrors(prev => { const n = { ...prev }; delete n[dataKey]; return n; });

    const buildNcDataset = (data: any[]) => ({
      labels: data.map((d: any) => d.monthLabel),
      datasets: [{ label: "Non-Chargeable £", data: data.map((d: any) => d.non_chargeable_gbp) }],
    });

    if (dataKey === "monthlyUtilisationCombo") {
      setLoadingKeys(prev => new Set([...prev, "monthlyUtilisationCombo"]));
      fetchMonthlyUtilisation(filters)
        .then(monthly => {
          setChartData((prev: any) => ({
            ...prev,
            monthlyUtilisationCombo: {
              labels: monthly.data.map((d: any) => d.monthLabel),
              chargeableHours: monthly.data.map((d: any) => d.chargeable_hours),
              non_chargeableHours: [],
              utilisationPct: monthly.data.map((d: any) => sanitiseUtilPct(d.util_pct, d.monthLabel)),
              plannedChargeableHours: monthly.data.map((d: any) => d.planned_chargeable_hours ?? 0),
              forecastUtilisationPct: monthly.data.map((d: any) => d.forecast_util_pct ?? 0),
            },
          }));
        })
        .catch(() => { setChartErrors(prev => ({ ...prev, monthlyUtilisationCombo: true })); })
        .finally(() => {
          setLoadingKeys(prev => { const n = new Set(prev); n.delete("monthlyUtilisationCombo"); return n; });
        });
    } else if (dataKey === "averageCor") {
      setLoadingKeys(prev => new Set([...prev, "averageCor"]));
      fetchAvgCor(filters)
        .then(avgCor => {
          setChartData((prev: any) => ({
            ...prev,
            averageCor: {
              labels: avgCor.data.map((d: any) => d.monthLabel),
              datasets: [{ label: "Average COR", data: avgCor.data.map((d: any) => d.avg_cor) }],
            },
          }));
        })
        .catch(() => { setChartErrors(prev => ({ ...prev, averageCor: true })); })
        .finally(() => {
          setLoadingKeys(prev => { const n = new Set(prev); n.delete("averageCor"); return n; });
        });
    } else if (dataKey === "nonChargeableHoursByActivity") {
      setLoadingKeys(prev => new Set([...prev, "nonChargeableHoursByActivity"]));
      fetchNonChargeableGbp(filters)
        .then(nc => {
          setChartData((prev: any) => ({ ...prev, nonChargeableHoursByActivity: buildNcDataset(nc.data) }));
        })
        .catch(() => {
          setChartErrors(prev => ({ ...prev, nonChargeableHoursByActivity: true }));
        })
        .finally(() => {
          setLoadingKeys(prev => { const n = new Set(prev); n.delete("nonChargeableHoursByActivity"); return n; });
        });
    } else if (dataKey === "utilisationByLocation") {
      setLoadingKeys(prev => new Set([...prev, "utilisationByLocation"]));
      fetchUtilisationByLocation(filters)
        .then(utilByLocation => {
          setChartData((prev: any) => ({ ...prev, utilisationByLocation: buildMonthlySeriesDataset(utilByLocation) }));
        })
        .catch(() => {
          setChartErrors(prev => ({ ...prev, utilisationByLocation: true }));
        })
        .finally(() => {
          setLoadingKeys(prev => { const n = new Set(prev); n.delete("utilisationByLocation"); return n; });
        });
    } else if (dataKey === "nonChargeableHoursByActivityBreakdown") {
      setLoadingKeys(prev => new Set([...prev, "nonChargeableHoursByActivityBreakdown"]));
      fetchNonChargeableHoursByActivity(filters)
        .then(activityHours => {
          setChartData((prev: any) => ({ ...prev, nonChargeableHoursByActivityBreakdown: buildMonthlySeriesDataset(activityHours) }));
        })
        .catch(() => {
          setChartErrors(prev => ({ ...prev, nonChargeableHoursByActivityBreakdown: true }));
        })
        .finally(() => {
          setLoadingKeys(prev => { const n = new Set(prev); n.delete("nonChargeableHoursByActivityBreakdown"); return n; });
        });
    } else if (dataKey === "nonChargeableGbpByService") {
      setLoadingKeys(prev => new Set([...prev, "nonChargeableGbpByService"]));
      fetchNonChargeableGbpByService(filters)
        .then(gbpByService => {
          setChartData((prev: any) => ({ ...prev, nonChargeableGbpByService: buildMonthlySeriesDataset(gbpByService) }));
        })
        .catch(() => {
          setChartErrors(prev => ({ ...prev, nonChargeableGbpByService: true }));
        })
        .finally(() => {
          setLoadingKeys(prev => { const n = new Set(prev); n.delete("nonChargeableGbpByService"); return n; });
        });
    } else if (dataKey === "utilisationByGrade") {
      setLoadingKeys(prev => new Set([...prev, "utilisationByGrade"]));
      fetchUtilisationByGrade(filters)
        .then(utilByGrade => {
          setChartData((prev: any) => ({ ...prev, utilisationByGrade: buildUtilByGradeDataset(utilByGrade) }));
        })
        .catch(() => {
          setChartErrors(prev => ({ ...prev, utilisationByGrade: true }));
        })
        .finally(() => {
          setLoadingKeys(prev => { const n = new Set(prev); n.delete("utilisationByGrade"); return n; });
        });
    } else if (dataKey === "burnoutRisk") {
      setLoadingKeys(prev => new Set([...prev, "burnoutRisk"]));
      fetchBurnoutRisk(filters)
        .then(burnoutRisk => {
          setChartData((prev: any) => ({
            ...prev,
            burnoutRisk: {
              labels: burnoutRisk.data.map((d: any) => d.monthLabel),
              datasets: [{ label: "At-Risk %", data: burnoutRisk.data.map((d: any) => d.at_risk_pct) }],
            },
          }));
        })
        .catch(() => {
          setChartErrors(prev => ({ ...prev, burnoutRisk: true }));
        })
        .finally(() => {
          setLoadingKeys(prev => { const n = new Set(prev); n.delete("burnoutRisk"); return n; });
        });
    }
  };


  const isAdminRole = userRole?.toLowerCase() === "admin";
  const visibleCharts = useMemo(() => getVisibleCharts(isAdminRole), [isAdminRole]);

  const chartContextSources: ChartContextSource[] = useMemo(
    () =>
      visibleCharts.map(cfg => ({
        id: cfg.id,
        title: cfg.title,
        description: cfg.description,
        getContextData: () => {
          const slice = chartData?.[cfg.dataKey];
          if (!slice) return null;
          if (cfg.dataKey === "monthlyUtilisationCombo") {
            // The only payload held as parallel arrays rather than datasets.
            return {
              labels: (slice.labels ?? []) as string[],
              series: [
                { name: "Chargeable hours", values: slice.chargeableHours ?? [] },
                { name: "Utilisation %", values: slice.utilisationPct ?? [] },
                { name: "Planned chargeable hours", values: slice.plannedChargeableHours ?? [] },
                { name: "Forecast utilisation %", values: slice.forecastUtilisationPct ?? [] },
              ],
            };
          }
          return contextFromDatasets(slice, UTILISATION_CHART_UNITS[cfg.dataKey]);
        },
      })),
    [visibleCharts, chartData]
  );
  const anyLoading = loadingKeys.size > 0;

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const saved = localStorage.getItem("prism:sidebar:open");
      return saved !== null ? saved === "true" : true;
    } catch { return true; }
  });

  const handleSidebarChange = (open: boolean) => {
    setSidebarOpen(open);
    try { localStorage.setItem("prism:sidebar:open", String(open)); } catch {}
  };

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={handleSidebarChange}>
      <div className="min-h-dvh flex w-full bg-background">
        <AppSidebar />

        <div className="flex-1 flex flex-col min-w-0 h-dvh overflow-hidden">
          <MobileTopBar title={metricTitle} />
          <main className="flex-1 flex overflow-hidden min-h-0">
            {metricSlug === "utilisation-rate" ? (
              <>
                <div className="flex-1 min-w-0 p-2 sm:p-3 space-y-4 sm:space-y-6 overflow-auto">
                  {noAccess ? (
                    <NoAccess />
                  ) : initialLoading ? (
                    <UtilisationSkeleton count={visibleCharts.length} />
                  ) : (
                    <>
                      {/* Filter Bar */}
                      <UtilisationFilterBar
                        filterOptions={filterOptions}
                        filters={filters}
                        onChange={setFilters}
                        isLoading={anyLoading || optionsLoading}
                      />

                      <div className="flex justify-end">
                        <Button asChild variant="outline" size="sm" className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                          <Link to="/metrics/utilisation-rate/drill-through" state={{ filters }}>
                            <Search className="h-4 w-4" />
                            Drill Through
                          </Link>
                        </Button>
                      </div>

                      {/* Charts Carousel */}
                      {chartData && (
                        <ChartCarousel
                          chartData={chartData}
                          visibleCharts={visibleCharts}
                          loadingKeys={loadingKeys}
                          chartErrors={chartErrors}
                          onRetry={retryChart}
                        />
                      )}
                    </>
                  )}
                </div>
                <ChatDock
                  open={isAssistantOpen}
                  onOpenChange={setIsAssistantOpen}
                  chartItems={chartContextSources}
                />
              </>
            ) : (
              <div className="flex-1 min-w-0 overflow-auto p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-6">
                  <BarChart3 className="h-7 w-7 shrink-0 text-primary sm:h-8 sm:w-8" />
                  <h1 className="text-2xl font-bold text-foreground sm:text-3xl">{metricTitle}</h1>
                </div>

                <div className="grid gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Metric Overview</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">
                        Detailed visualization and analysis for {metricTitle} will be displayed here.
                        This is a mock interface showing the structure of individual metric pages.
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Data Analysis</CardTitle>
                    </CardHeader>
                    <CardContent className="h-64 flex items-center justify-center bg-muted/30 rounded-lg">
                      <p className="text-muted-foreground">Chart placeholder for {metricTitle}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Key Insights</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-muted-foreground">
                        <li>• Insight 1 for {metricTitle}</li>
                        <li>• Insight 2 for {metricTitle}</li>
                        <li>• Insight 3 for {metricTitle}</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default MetricDetail;

function readSavedUtilisationFilters(): UtilisationFilters | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(UTILISATION_FILTERS_STORAGE_KEY) ?? "null");
    if (!parsed || typeof parsed !== "object") return null;
    const strings = (value: unknown) => Array.isArray(value) && value.every(item => typeof item === "string") ? value : [];
    return {
      legacy_location: strings(parsed.legacy_location),
      employees: strings(parsed.employees),
      emp_categories: strings(parsed.emp_categories),
      service_lines: strings(parsed.service_lines),
      date_from: typeof parsed.date_from === "string" ? parsed.date_from : null,
      date_to: typeof parsed.date_to === "string" ? parsed.date_to : null,
    };
  } catch { return null; }
}
